// Lib
import React, { MutableRefObject } from 'react';
import PropTypes from 'prop-types';
import { Editor as TiptapEditor } from '@tiptap/core';

// Utils
import { length, prop } from '../../../common/utils/immutableHelper';
import { isReadOnly } from '../../../common/permissions/permissionUtil';

// Components
import AnchorButton from '../../components/buttons/AnchorButton';
import ElementReactionAddButton from '../reactions/ElementReactionAddButton';

// Styles
import './CommentTools.scss';
import { useTranslator } from '../../components/i18n/useTranslator';

const getEditorStateRawContent = (editorState: TiptapEditor | null) => {
    if (!editorState) return null;

    if (!editorState.getJSON) return null;

    return editorState.getJSON();
};

type SaveButtonProps = {
    updateComment: (rawText: object) => void;
    editorInstanceRef: MutableRefObject<TiptapEditor | null>;
};

const SaveButton = (props: SaveButtonProps) => {
    const translate = useTranslator('elements', 'elementTypes.comment');

    const onSave = (e: MouseEvent) => {
        e.stopPropagation();

        const { updateComment, editorInstanceRef } = props;

        const editorState = editorInstanceRef?.current;

        const rawText = getEditorStateRawContent(editorState);

        if (!rawText) return;

        updateComment(rawText);
    };

    return <AnchorButton onMouseDown={onSave}>{translate('tools.save')}</AnchorButton>;
};

SaveButton.propTypes = {
    updateComment: PropTypes.func,
    editorInstanceRef: PropTypes.object,
};

type CommentToolsProps = {
    _id: string;
    userId: string;
    currentUserId: string;
    startEditing: () => void;
    content: object;
    setKeepToolsVisible: (keepToolsVisible: boolean) => void;
    permissions: number;
    isUpdating: boolean;
    updateComment: (rawText: object) => void;
    editorInstanceRef: MutableRefObject<TiptapEditor | null>;
};

const CommentTools = ({
    _id,
    currentUserId,
    userId,
    startEditing,
    content,
    setKeepToolsVisible,
    permissions,
    isUpdating,
    updateComment,
    editorInstanceRef,
}: CommentToolsProps) => {
    const translate = useTranslator('elements', 'elementTypes.comment');
    const canEditComment = currentUserId === userId;
    const hasReactions = length(prop('reactions', content) || []) > 0;

    if (isReadOnly(permissions)) return null;

    return (
        <div className="CommentTools">
            {canEditComment &&
                (isUpdating ? (
                    <SaveButton updateComment={updateComment} editorInstanceRef={editorInstanceRef} />
                ) : (
                    <AnchorButton onClickFn={startEditing}>{translate('tools.edit')}</AnchorButton>
                ))}
            {!canEditComment && !hasReactions && (
                <ElementReactionAddButton elementId={_id} onPopupToggleCb={setKeepToolsVisible} />
            )}
        </div>
    );
};

export default CommentTools;
